import { useEffect, useState } from "react";
import { Loader2, Sparkles, Wand2, Smartphone, Monitor, Palette, Star } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs";

export const NEW_BLOCK_TYPES = [
  { type: "hero", label: "Hero (visuel + titre + CTA)", default: { title: "Titre accrocheur", subtitle: "Une phrase qui donne envie", cta: "Je découvre", url: "https://", src: "", bgColor: "#14101c", color: "#ffffff" } },
  { type: "features", label: "Avantages (3 colonnes)", default: { title: "Pourquoi nous choisir", items: [{ title: "Avantage 1", text: "Détail" }, { title: "Avantage 2", text: "Détail" }, { title: "Avantage 3", text: "Détail" }] } },
  { type: "testimonial", label: "Témoignage", default: { quote: "Une formation qui a changé notre façon de travailler.", author: "Prénom Nom", role: "Dirigeant, Société" } },
  { type: "footer", label: "Pied de page légal", default: { company: "", address: "", legal: "Vous recevez cet email car vous êtes client ou contact. Conformément au RGPD, vous pouvez vous désabonner à tout moment.", bgColor: "#f0f0f2" } },
];

export const DEFAULT_THEME = { primary: "#f7c935", secondary: "#14101c", background: "#f4f4f6", surface: "#ffffff", text: "#222222", font: "Arial, Helvetica, sans-serif" };

/* Panneau « Concevoir avec l'IA » : prompt → sujet + thème + blocs */
export function AiDesignPanel({ api, societeId, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [goal, setGoal] = useState("conversion");
  const [useMedia, setUseMedia] = useState(true);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    if (prompt.trim().length < 5) { toast.error("Décrivez l'email souhaité"); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketing/mail-designer/generate", { societe_id: societeId, prompt, goal, use_media: useMedia });
      onApply(r.data);
      toast.success(`Design généré : ${r.data.blocks.length} blocs`);
    } catch (e) { toast.error(e.response?.data?.detail || "Génération impossible"); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-[#f7c935]/30 bg-[#f7c935]/5 p-3 space-y-2" data-testid="ai-design-panel">
      <div className="text-xs font-bold text-[#f7c935] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Concevoir avec l'IA</div>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="ai-design-prompt"
        placeholder="Ex. : Lancement d'une formation IA pour dirigeants de TPE, 2 jours à Lyon, éligible CPF, ambiance premium sombre et dorée, un seul bouton d'inscription…" />
      <div className="flex flex-wrap gap-2 items-center">
        <select value={goal} onChange={(e) => setGoal(e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="ai-design-goal">
          <option value="conversion">Objectif : inscriptions / ventes</option><option value="information">Objectif : informer</option><option value="fidelisation">Objectif : fidéliser</option><option value="evenement">Objectif : événement</option>
        </select>
        <label className="text-[11px] text-slate-300 flex items-center gap-1"><input type="checkbox" checked={useMedia} onChange={(e) => setUseMedia(e.target.checked)} data-testid="ai-design-use-media" /> Utiliser mes photos</label>
        <button onClick={generate} disabled={busy} className="ml-auto px-3 py-1.5 rounded bg-[#f7c935] text-[#0b0811] text-xs font-bold flex items-center gap-1 disabled:opacity-50" data-testid="ai-design-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Générer le design
        </button>
      </div>
    </div>
  );
}

export function ThemeEditor({ theme, onChange }) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const keys = [["primary", "Accent / CTA"], ["secondary", "Fond hero"], ["background", "Fond page"], ["surface", "Fond blocs"], ["text", "Texte"]];
  return (
    <div className="rounded-lg border border-white/10 p-3" data-testid="theme-editor">
      <div className="text-[10px] text-slate-500 uppercase mb-2 flex items-center gap-1"><Palette className="w-3 h-3" /> Charte graphique</div>
      <div className="flex flex-wrap gap-3 items-end">
        {keys.map(([k, lbl]) => (
          <label key={k} className="text-[10px] text-slate-400 flex flex-col gap-1">{lbl}
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(t[k]) ? t[k] : "#000000"} onChange={(e) => onChange({ ...t, [k]: e.target.value })} className="w-9 h-7 bg-transparent border border-white/10 rounded" data-testid={`theme-${k}`} />
          </label>
        ))}
        <label className="text-[10px] text-slate-400 flex flex-col gap-1 flex-1 min-w-[140px]">Police
          <select value={t.font} onChange={(e) => onChange({ ...t, font: e.target.value })} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="theme-font">
            <option value="Arial, Helvetica, sans-serif">Arial</option><option value="Georgia, serif">Georgia</option><option value="'Trebuchet MS', sans-serif">Trebuchet</option><option value="Verdana, sans-serif">Verdana</option><option value="'Courier New', monospace">Courier</option>
          </select>
        </label>
      </div>
    </div>
  );
}

export function MediaPicker({ api, societeId, value, onChange }) {
  const [media, setMedia] = useState([]);
  useEffect(() => { if (societeId) api.get("/marketing/media", { params: { societe_id: societeId, kind: "image" } }).then((r) => setMedia(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [societeId]);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid="media-picker">
      <option value="">— Image de la médiathèque —</option>
      {media.map((m) => <option key={m.media_id} value={m.url}>{m.title || m.filename}</option>)}
      {value && !media.some((m) => m.url === value) && <option value={value}>URL personnalisée</option>}
    </select>
  );
}

export function ImproveButton({ api, text, onText }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/marketing/mail-designer/improve", { text }); onText(r.data.text); toast.success("Texte amélioré"); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible"); }
    setBusy(false);
  };
  return <button type="button" onClick={run} disabled={busy || !text} className="text-[10px] text-[#f7c935] hover:underline flex items-center gap-1 disabled:opacity-40" data-testid="improve-text-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Améliorer avec l'IA</button>;
}

export function PreviewToggle({ mode, onChange }) {
  return (
    <div className="flex gap-1">
      {[["desktop", Monitor], ["mobile", Smartphone]].map(([m, Icon]) => (
        <button key={m} onClick={() => onChange(m)} className={`px-2 py-1 rounded text-[10px] ${mode === m ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:bg-white/5"}`} data-testid={`preview-${m}`}><Icon className="w-3.5 h-3.5" /></button>
      ))}
    </div>
  );
}

export function DefaultStar({ isDefault, onClick }) {
  return <button onClick={onClick} title="Modèle par défaut de l'agent éditorial" className={`p-1 rounded ${isDefault ? "text-[#f7c935]" : "text-slate-500 hover:text-slate-300"}`} data-testid="template-default-btn"><Star className={`w-3.5 h-3.5 ${isDefault ? "fill-[#f7c935]" : ""}`} /></button>;
}
